import type { EvidenceLine } from '../onboardingV2AssetsTypes';

/**
 * Parse the `evidence_response` markdown field into the typed-line shape used
 * by `MockEvidenceResponse` for its staggered reveal animation.
 *
 * Recognises:
 *   - `## Heading` or short trailing-colon lines as `subheading`
 *   - `- item` / `* item` / `• item` as `bullet`
 *   - blockquote lines (`> ...`) and final-line citations (Author. (Year). ...) as `source`
 *   - everything else as `paragraph`
 *
 * Mirrors the heuristics in `parseExampleNoteIntoSections` but with the four
 * line kinds the legacy `EVIDENCE_LINES` array used.
 */

const HEADING_HASHES_PATTERN = /^#+\s*/;
const BULLET_PATTERN = /^\s*[-*•]\s+/;
const TRAILING_COLON_PATTERN = /:\s*$/;
const BLOCKQUOTE_PATTERN = /^>\s+/;
const INLINE_EMPHASIS_PATTERN = /[*_`]/;
const HEADER_MAX_LENGTH = 60;
const SENTENCE_TERMINATOR_PATTERN = /[.!?]$/;
/** Citation-shaped trailing line, e.g. "CSANZ Guidelines (2023). Heart, Lung..." */
const CITATION_PATTERN = /\(\d{4}\)/;

function looksLikeSubheading(text: string): boolean {
  if (text.startsWith('#')) return true;
  const cleaned = text.replace(TRAILING_COLON_PATTERN, '').trim();
  if (!cleaned) return false;
  if (cleaned.length > HEADER_MAX_LENGTH) return false;
  if (INLINE_EMPHASIS_PATTERN.test(cleaned)) return false;
  if (SENTENCE_TERMINATOR_PATTERN.test(cleaned)) return false;
  return true;
}

function parseLine(line: string, isLastNonEmpty: boolean): EvidenceLine | null {
  const trimmed = line.trim();
  if (!trimmed) return null;

  if (BLOCKQUOTE_PATTERN.test(trimmed)) {
    return { type: 'source', text: trimmed.replace(BLOCKQUOTE_PATTERN, '').trim() };
  }

  if (BULLET_PATTERN.test(trimmed)) {
    return { type: 'bullet', text: trimmed.replace(BULLET_PATTERN, '').trim() };
  }

  if (looksLikeSubheading(trimmed)) {
    const headerText = trimmed
      .replace(HEADING_HASHES_PATTERN, '')
      .replace(TRAILING_COLON_PATTERN, '')
      .trim();
    if (!headerText) return null;
    return { type: 'subheading', text: headerText };
  }

  if (isLastNonEmpty && CITATION_PATTERN.test(trimmed)) {
    return { type: 'source', text: trimmed };
  }

  return { type: 'paragraph', text: trimmed };
}

export function parseEvidenceResponseIntoLines(markdown: string): EvidenceLine[] {
  if (!markdown) return [];

  const rawLines = markdown.split('\n');
  // Find the index of the last non-empty line so we can heuristically tag a
  // trailing citation line as a `source` even without a `>` prefix.
  let lastNonEmptyIdx = -1;
  for (let i = rawLines.length - 1; i >= 0; i -= 1) {
    if (rawLines[i]?.trim()) {
      lastNonEmptyIdx = i;
      break;
    }
  }

  const out: EvidenceLine[] = [];
  rawLines.forEach((line, i) => {
    const parsed = parseLine(line, i === lastNonEmptyIdx);
    if (parsed) out.push(parsed);
  });
  return out;
}
